"use client";

import { useEffect, useRef, useState } from "react";
import {
  ArrowLeft,
  ChevronRight,
  FileText,
  Logs,
  Palette,
  Repeat2,
  X,
} from "lucide-react";
import TaskOptionsView from "@/components/tasks/options/TaskOptionsView/TaskOptionsView";
import PatternEditorModal from "@/components/tasks/patterns/PatternEditorModal/PatternEditorModal";
import TaskStatusForm from "@/components/tasks/status/TaskStatusForm/TaskStatusForm";
import TaskDetailsHeader from "@/components/tasks/details/TaskDetailsHeader/TaskDetailsHeader";
import TaskInfoSection from "@/components/tasks/details/TaskInfoSection/TaskInfoSection";
import TaskNoteEditor from "@/components/tasks/details/TaskNoteEditor/TaskNoteEditor";
import {
  countTaskLogLines,
  TaskLogEditor,
} from "@/components/tasks/details/TaskLogEditor/TaskLogEditor";
import ListSelector, {
  type ListOption,
} from "@/components/foundations/lists/ListSelector/ListSelector";
import RepeatOptions from "@/components/tasks/repeat/RepeatOptions/RepeatOptions";
import { type RepeatRule } from "@/components/tasks/repeat/RepeatOptions/repeat-options-types";
import TaskScheduleForm, {
  parseScheduleDuration,
} from "@/components/tasks/schedule/TaskScheduleForm/TaskScheduleForm";
import { useLocation } from "wouter";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { trpc } from "@/lib/trpc";
import { createTaskScheduleValue } from "@/lib/taskTiming";
import { DEFAULT_AREA_COLOR, resolveTaskColors } from "@/lib/listColors";
import { minutesToTimeString, formatDateKey } from "@/lib/time";
import { toast } from "sonner";
import {
  dateAndTimeToHHMM,
  combineDateAndTime,
  resultDurationMinutes,
} from "@/pages/TaskResults";
import {
  TaskColorEditor,
  type TaskColorDraft,
} from "@/components/tasks/options/TaskColorEditor/TaskColorEditor";
import { useTaskEditorModel } from "@/lib/taskEditorModel";
import {
  normalizeTaskVisibleFields,
  type TaskVisibleFields,
} from "@shared/taskVisibleFields";

// ─── Types ───────────────────────────────────────────────────────────────────

export type TaskEditSheetTask = {
  id: number;
  title: string;
  details?: string | null;
  notes?: string | null;
  date?: string | null;
  status: "pending" | "done" | "skipped";
  startMinutes?: number | null;
  durationMin?: number | null;
  alertEnabled?: boolean;
  startedAt?: Date | string | null;
  completedAt?: Date | string | null;
  log?: string | null;
  barColor?: string | null;
  bgColor?: string | null;
  listId?: number | null;
  templateId?: number | null;
  listName?: string | null;
  listColor?: string | null;
  listBgColor?: string | null;
  visibleFields?: TaskVisibleFields | null;
};

type View = "main" | "notes" | "log" | "options" | "display" | "repeat";
type DetailReturnView = "main" | "options";
type VisibleFields = TaskVisibleFields;

type Props = {
  task: TaskEditSheetTask;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAreaClick: () => void;
  /** @deprecated kept for API compatibility — navigation is now internal */
  onScheduleClick?: () => void;
  /** @deprecated kept for API compatibility — status view is now internal */
  onResultsClick?: () => void;
  areaPickerOpen?: boolean;
  onAreaPickerOpenChange?: (open: boolean) => void;
  onDuplicated?: (id: number) => void;
};

const STATUS_SECTION_OPEN_KEY = "task-edit-status-open";
const SCHEDULE_SECTION_OPEN_KEY = "task-edit-schedule-open";

function readStatusSectionOpen() {
  if (typeof window === "undefined") return false;
  return window.localStorage.getItem(STATUS_SECTION_OPEN_KEY) === "true";
}

function readScheduleSectionOpen() {
  if (typeof window === "undefined") return false;
  return window.localStorage.getItem(SCHEDULE_SECTION_OPEN_KEY) === "true";
}

function repairReversedCompletion(startedAt: Date, completedAt: Date) {
  if (completedAt >= startedAt) return completedAt;
  const repaired = new Date(startedAt);
  repaired.setHours(
    completedAt.getHours(),
    completedAt.getMinutes(),
    completedAt.getSeconds(),
    completedAt.getMilliseconds()
  );
  if (repaired < startedAt) repaired.setDate(repaired.getDate() + 1);
  return repaired;
}

// ─── Main component ───────────────────────────────────────────────────────────

export function TaskEditSheet({
  task,
  open,
  onOpenChange,
  onAreaClick,
  areaPickerOpen = false,
  onAreaPickerOpenChange,
  onDuplicated,
}: Props) {
  const [, setLocation] = useLocation();
  const listsQuery = trpc.lists.list.useQuery();
  const [areaOverride, setAreaOverride] = useState<ListOption | null>(null);
  useEffect(() => {
    setAreaOverride(null);
  }, [task.id]);
  const visibleTask = areaOverride
    ? {
        ...task,
        listId: areaOverride.id,
        listName: areaOverride.name,
        listColor: areaOverride.color ?? task.listColor,
        listBgColor: areaOverride.bgColor ?? task.listBgColor,
      }
    : task;
  // ── View stack ──
  const [view, setView] = useState<View>("main");
  const [patternModalOpen, setPatternModalOpen] = useState(false);
  const [patternModalTemplateId, setPatternModalTemplateId] = useState<
    number | null
  >(null);
  const [detailReturnView, setDetailReturnView] =
    useState<DetailReturnView>("main");
  const [visibleFields, setVisibleFields] = useState<VisibleFields>(() =>
    normalizeTaskVisibleFields(task.visibleFields)
  );
  const viewScrollRef = useRef<HTMLDivElement>(null);

  function openFieldDetails(
    nextView: "notes" | "log" | "repeat",
    returnView: DetailReturnView
  ) {
    setDetailReturnView(returnView);
    setView(nextView);
  }

  function toggleFieldVisibility(field: keyof VisibleFields) {
    const previous = visibleFields;
    const next = { ...previous, [field]: !previous[field] };
    setVisibleFields(next);
    updateVisibleFields.mutate(
      { id: task.id, visibleFields: next },
      { onError: () => setVisibleFields(previous) }
    );
  }

  // ── Schedule local state ──
  const [scheduleOpen, setScheduleOpen] = useState(readScheduleSectionOpen);
  const [statusOpen, setStatusOpen] = useState(readStatusSectionOpen);
  const [statusOverride, setStatusOverride] = useState<
    "pending" | "done" | "skipped" | null
  >(null);
  const { model, updateSchedule, updateStatus } = useTaskEditorModel(task);
  const scheduleDate = model.schedule.date ?? "";
  const startTime = model.schedule.startTime ?? "";
  const durationMin =
    model.schedule.durationMin == null
      ? ""
      : String(model.schedule.durationMin);
  const alertEnabled = model.schedule.alertEnabled;
  const setScheduleDate = (value: string) =>
    updateSchedule({ date: value || null });
  const setStartTime = (value: string) =>
    updateSchedule({ startTime: value || null });
  const setDurationMin = (value: string) => {
    updateSchedule({ durationMin: value ? Number(value) : null });
  };
  const setAlertEnabled = (value: boolean) =>
    updateSchedule({ alertEnabled: value });

  // ── Status/log local state (save-on-button only) ──
  const startedAtTime = model.status.startedAt
    ? dateAndTimeToHHMM(model.status.startedAt)
    : "";
  const endedAtTime = model.status.completedAt
    ? dateAndTimeToHHMM(model.status.completedAt)
    : "";
  const resultDuration =
    model.status.durationMin == null ? "" : String(model.status.durationMin);
  const statusStartDate = model.status.startedAt
    ? formatDateKey(model.status.startedAt)
    : (model.status.date ?? "");
  const statusEndDate = model.status.completedAt
    ? formatDateKey(model.status.completedAt)
    : statusStartDate;
  const statusStartDateForTime = statusStartDate || formatDateKey(new Date());
  const statusEndDateForTime = statusEndDate || statusStartDateForTime;
  const setStartedAtTime = (value: string) =>
    updateStatus({
      startedAt: value
        ? combineDateAndTime(statusStartDateForTime, value)
        : null,
    });
  const setEndedAtTime = (value: string) =>
    updateStatus({
      completedAt: value
        ? combineDateAndTime(statusEndDateForTime, value)
        : null,
    });
  const setResultDuration = (value: string) =>
    updateStatus({ durationMin: value ? Number(value) : null });
  const [notes, setNotes] = useState(task.notes ?? "");
  const [logText, setLogText] = useState("");
  const [repeatRule, setRepeatRule] = useState<RepeatRule>({ kind: "none" });
  const [savedRepeatRule, setSavedRepeatRule] = useState<RepeatRule>({
    kind: "none",
  });
  const [excludedRepeatKeys, setExcludedRepeatKeys] = useState<string[]>([]);
  const [savedExcludedRepeatKeys, setSavedExcludedRepeatKeys] = useState<
    string[]
  >([]);
  const savedLogRef = useRef(task.log ?? "");

  // ── Inline title/description editing ──
  const [titleDraft, setTitleDraft] = useState(task.title);
  const [detailsDraft, setDetailsDraft] = useState(task.details ?? "");
  // ── Display/color draft state (auto-saves on exit when dirty) ──
  const [colorDraft, setColorDraft] = useState<TaskColorDraft>({
    inheritView: task.barColor == null && task.bgColor == null,
    barColor: (task.barColor ?? null) as
      | import("@/lib/listColors").ListColorKey
      | null,
    bgColor: (task.bgColor ?? null) as
      | import("@/lib/listColors").ListColorKey
      | null,
  });
  const colorOriginalRef = useRef<TaskColorDraft>({
    inheritView: task.barColor == null && task.bgColor == null,
    barColor: (task.barColor ?? null) as
      | import("@/lib/listColors").ListColorKey
      | null,
    bgColor: (task.bgColor ?? null) as
      | import("@/lib/listColors").ListColorKey
      | null,
  });

  // ── Modals ──

  const utils = trpc.useUtils();
  const repeatQuery = trpc.tasks.getRepeatSeries.useQuery(
    { id: task.id },
    { enabled: open && task.id > 0 }
  );
  const colors = resolveTaskColors({
    barColor: task.barColor ?? null,
    bgColor: task.bgColor ?? null,
    listColor: visibleTask.listColor ?? DEFAULT_AREA_COLOR,
    listBgColor: visibleTask.listBgColor ?? null,
  });
  const listColors = resolveTaskColors({
    barColor: null,
    bgColor: null,
    listColor: visibleTask.listColor ?? DEFAULT_AREA_COLOR,
    listBgColor: visibleTask.listBgColor ?? null,
  });
  const hasCustomColors = task.barColor != null || task.bgColor != null;

  // ── Invalidation helper ──
  function invalidateTaskLists() {
    utils.tasks.get.invalidate({ id: task.id });
    utils.tasks.listInbox.invalidate();
    utils.tasks.listByList.invalidate();
    utils.lists.overview.invalidate();
    utils.day.monthSummary.invalidate();
    utils.day.list.invalidate();
    utils.day.monthSummary.invalidate();
  }

  function clientTimestamp() {
    return new Date().toISOString();
  }

  // ── Reset on open/close ──
  useEffect(() => {
    if (open) {
      setView("main");
      setStatusOverride(null);
      setTitleDraft(task.title);
      setDetailsDraft(task.details ?? "");
      setScheduleOpen(readScheduleSectionOpen());
      setStatusOpen(readStatusSectionOpen());
      setScheduleDate(task.date ?? "");
      setStartTime(
        task.startMinutes != null ? minutesToTimeString(task.startMinutes) : ""
      );
      setDurationMin(String(task.durationMin ?? ""));
      setAlertEnabled(task.alertEnabled ?? false);
      setNotes(task.notes ?? "");
      setVisibleFields(normalizeTaskVisibleFields(task.visibleFields));
      savedLogRef.current = task.log ?? "";
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(STATUS_SECTION_OPEN_KEY, String(statusOpen));
  }, [statusOpen]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(
      SCHEDULE_SECTION_OPEN_KEY,
      String(scheduleOpen)
    );
  }, [scheduleOpen]);

  // ── Sync title/details draft when task prop changes ──
  useEffect(() => {
    setTitleDraft(task.title);
    setDetailsDraft(task.details ?? "");
  }, [task.title, task.details]);

  useEffect(() => {
    setScheduleDate(task.date ?? "");
  }, [task.id, task.date]);

  useEffect(() => {
    setNotes(task.notes ?? "");
  }, [task.id, task.notes]);

  useEffect(() => {
    setVisibleFields(normalizeTaskVisibleFields(task.visibleFields));
  }, [task.id, task.visibleFields]);

  useEffect(() => {
    setLogText(task.log ?? "");
    savedLogRef.current = task.log ?? "";
  }, [task.id, task.log]);

  useEffect(() => {
    if (!open || repeatQuery.data === undefined) return;
    const rule = (repeatQuery.data?.series.rule ?? {
      kind: "none",
    }) as RepeatRule;
    const excluded =
      repeatQuery.data?.occurrences
        .filter(occurrence => !occurrence.enabled)
        .map(occurrence => occurrence.occurrenceKey) ?? [];
    setRepeatRule(rule);
    setSavedRepeatRule(rule);
    setExcludedRepeatKeys(excluded);
    setSavedExcludedRepeatKeys(excluded);
  }, [open, task.id, repeatQuery.data]);

  // ── Hydrate status view fields when entering status view ──
  useEffect(() => {
    if (!statusOpen) return;
    const startedAt = task.startedAt as unknown as Date | null;
    const completedAt = task.completedAt as unknown as Date | null;
    setStartedAtTime(dateAndTimeToHHMM(startedAt));
    setEndedAtTime(dateAndTimeToHHMM(completedAt));
    setResultDuration(resultDurationMinutes(startedAt, completedAt));
    setLogText(task.log ?? "");
    savedLogRef.current = task.log ?? "";
  }, [statusOpen, task.id, task.startedAt, task.completedAt, task.log]);

  // ── Mutations ──
  const markDone = trpc.tasks.markDone.useMutation({
    onSuccess: invalidateTaskLists,
    onError: () => setStatusOverride(null),
  });
  const markPending = trpc.tasks.markPending.useMutation({
    onSuccess: invalidateTaskLists,
    onError: () => {
      setStatusOverride(null);
      invalidateTaskLists();
    },
  });
  const markSkipped = trpc.tasks.markSkipped.useMutation({
    onSuccess: invalidateTaskLists,
    onError: () => {
      setStatusOverride(null);
      invalidateTaskLists();
    },
  });
  const duplicateTask = trpc.tasks.duplicate.useMutation({
    onSuccess: result => {
      invalidateTaskLists();
      onDuplicated?.(result.id);
      toast.success("Task duplicated");
    },
  });
  const deleteTask = trpc.tasks.remove.useMutation({
    onSuccess: () => {
      invalidateTaskLists();
      onOpenChange(false);
      toast.success("Task deleted");
    },
  });
  const updateTask = trpc.tasks.update.useMutation({
    onSuccess: invalidateTaskLists,
  });
  const updateVisibleFields = trpc.tasks.updateVisibleFields.useMutation({
    onSuccess: result => {
      setVisibleFields(normalizeTaskVisibleFields(result.visibleFields));
      utils.tasks.get.setData({ id: task.id }, current =>
        current ? { ...current, visibleFields: result.visibleFields } : current
      );
      invalidateTaskLists();
    },
  });
  const saveRepeatSeries = trpc.tasks.saveRepeatSeries.useMutation({
    onSuccess: result => {
      const rule = result.rule as RepeatRule;
      const excluded = result.occurrences
        .filter(occurrence => !occurrence.enabled)
        .map(occurrence => occurrence.occurrenceKey);
      setRepeatRule(rule);
      setSavedRepeatRule(rule);
      setExcludedRepeatKeys(excluded);
      setSavedExcludedRepeatKeys(excluded);
      void repeatQuery.refetch();
      invalidateTaskLists();
      toast.success("Repeat schedule saved");
    },
    onError: error => toast.error(error.message),
  });

  useEffect(() => {
    if (!open || !model.status.startedAt || !model.status.completedAt) return;
    if (model.status.completedAt >= model.status.startedAt) return;
    const completedAt = repairReversedCompletion(
      model.status.startedAt,
      model.status.completedAt
    );
    updateStatus({
      completedAt,
      durationMin: Math.round(
        (completedAt.getTime() - model.status.startedAt.getTime()) / 60_000
      ),
    });
    updateTask.mutate({
      id: task.id,
      completedAt,
      clientTimestamp: clientTimestamp(),
    });
  }, [open, model.status.startedAt, model.status.completedAt]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Title/description save on blur ──
  function saveTitle() {
    const trimmed = titleDraft.trim();
    if (!trimmed || trimmed === task.title) return;
    updateTask.mutate({
      id: task.id,
      title: trimmed,
      date: task.date ?? null,
      clientTimestamp: clientTimestamp(),
    });
  }

  function saveDetails(nextDetails: string | null) {
    updateTask.mutate({
      id: task.id,
      details: nextDetails,
      date: task.date ?? null,
      clientTimestamp: clientTimestamp(),
    });
  }

  function openDisplayView() {
    const draft = {
      inheritView: task.barColor == null && task.bgColor == null,
      barColor: (task.barColor ?? null) as
        | import("@/lib/listColors").ListColorKey
        | null,
      bgColor: (task.bgColor ?? null) as
        | import("@/lib/listColors").ListColorKey
        | null,
    };
    setColorDraft(draft);
    colorOriginalRef.current = draft;
    setView("display");
  }

  // ── Log save (button only) ──
  function saveLog() {
    const next = logText.trim() === "" ? null : logText;
    const current =
      savedLogRef.current.trim() === "" ? null : savedLogRef.current;
    if (next !== current) {
      updateTask.mutate({
        id: task.id,
        log: next,
        date: task.date ?? null,
        clientTimestamp: clientTimestamp(),
      });
      savedLogRef.current = logText;
    }
  }

  // ── Status view: started/ended/duration helpers ──
  const resultDate = statusStartDate;
  const resultEndDate = statusEndDate;
  const resultDateForTime = statusStartDateForTime;

  function saveDuration() {
    if (!startedAtTime) return;
    const raw = resultDuration.trim();
    if (raw === "") return;
    const minutes = Math.max(0, parseInt(raw, 10) || 0);
    const startedAt = combineDateAndTime(statusStartDateForTime, startedAtTime);
    const completedAt = new Date(startedAt.getTime() + minutes * 60000);
    updateStatus({ completedAt, durationMin: minutes });
    updateTask.mutate({
      id: task.id,
      completedAt,
      clientTimestamp: clientTimestamp(),
    });
  }

  // ── Schedule summary ──

  const hasNotes = Boolean(notes.trim());
  const logEntryCount = countTaskLogLines(task.log);
  const localStartedAt = model.status.startedAt;
  const localCompletedAt = model.status.completedAt;
  const currentStatus = statusOverride ?? model.status.value;
  const anyModalOpen = areaPickerOpen;
  const isSubView = view !== "main";

  useEffect(() => {
    viewScrollRef.current?.scrollTo({ top: 0, left: 0 });
  }, [view]);

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          side="bottom"
          className="rounded-t-2xl flex max-h-[92dvh] flex-col overflow-hidden"
          showClose={false}
          onPointerDownOutside={
            anyModalOpen ? e => e.preventDefault() : undefined
          }
          onInteractOutside={anyModalOpen ? e => e.preventDefault() : undefined}
          onFocusOutside={anyModalOpen ? e => e.preventDefault() : undefined}
        >
          {/* Slide container — flex row, outer clips, each panel is 100% wide */}
          <div
            ref={viewScrollRef}
            className="ui-task-edit-viewport min-h-0 flex-1 overflow-x-hidden overflow-y-auto"
          >
            <div
              className="ui-task-edit-track flex w-full transition-transform duration-300 ease-[cubic-bezier(0.23,1,0.32,1)]"
              style={{
                transform: isSubView ? "translateX(-100%)" : "translateX(0%)",
              }}
            >
              {/* ── MAIN VIEW ── */}
              <div className="w-full shrink-0">
                <div className="mx-auto w-full max-w-2xl">
                  <SheetHeader className="sticky top-0 z-20 bg-background text-left">
                    <SheetTitle className="sr-only">
                      {titleDraft || "Task details"}
                    </SheetTitle>
                    <TaskDetailsHeader
                      listName={visibleTask.listName ?? "Inbox"}
                      colors={listColors}
                      onAreaClick={onAreaClick}
                      onOptionsClick={() => setView("options")}
                    />
                    <TaskInfoSection
                      value={{ title: titleDraft, description: detailsDraft }}
                      savedValue={{
                        title: task.title,
                        description: task.details ?? "",
                      }}
                      colors={colors}
                      onChange={(next, reason) => {
                        if (reason === "title") setTitleDraft(next.title);
                        else setDetailsDraft(next.description);
                      }}
                      onCommit={(next, reason) => {
                        if (reason === "title") saveTitle();
                        else saveDetails(next.description.trim() || null);
                      }}
                      onDisplayClick={openDisplayView}
                    />
                  </SheetHeader>

                  <div className="space-y-3 px-4 pb-6">
                    <TaskScheduleForm
                      open={scheduleOpen}
                      onOpenChange={setScheduleOpen}
                      value={createTaskScheduleValue({
                        date: scheduleDate,
                        startTime,
                        durationMinutes: durationMin,
                        alertEnabled,
                      })}
                      onChange={(next, reason) => {
                        updateSchedule({
                          date: next.timing.startDate || null,
                          startTime: next.timing.startTime || null,
                          durationMin: next.timing.durationMinutes
                            ? Number(next.timing.durationMinutes)
                            : null,
                          alertEnabled: next.alertEnabled,
                        });
                        if (reason === "duration") return;
                        updateTask.mutate({
                          id: task.id,
                          date: next.timing.startDate || null,
                          startTime: next.timing.startTime || null,
                          durationMin: next.timing.durationMinutes
                            ? Number(next.timing.durationMinutes)
                            : null,
                          alertEnabled: next.alertEnabled,
                          clientTimestamp: clientTimestamp(),
                        });
                      }}
                      onCommit={next => {
                        const duration = parseScheduleDuration(
                          next.timing.durationMinutes
                        );
                        if (duration !== (task.durationMin ?? null)) {
                          updateTask.mutate({
                            id: task.id,
                            durationMin: duration,
                            clientTimestamp: clientTimestamp(),
                          });
                        }
                      }}
                      colors={colors}
                      emptyLabel="No schedule"
                    />

                    {/* Status section */}
                    <TaskStatusForm
                      value={{
                        status: currentStatus as "pending" | "done" | "skipped",
                        startedAt: localStartedAt,
                        completedAt: localCompletedAt,
                        timing: {
                          startDate: resultDate,
                          endDate: resultEndDate || resultDate,
                          startTime: startedAtTime,
                          endTime: endedAtTime,
                          durationMinutes: resultDuration,
                        },
                      }}
                      schedule={createTaskScheduleValue({
                        date: scheduleDate,
                        startTime,
                        durationMinutes: durationMin,
                        alertEnabled,
                      })}
                      colors={colors}
                      open={statusOpen}
                      onOpenChange={setStatusOpen}
                      onChange={(next, reason) => {
                        const durationValue = next.timing.durationMinutes.trim()
                          ? Math.max(
                              0,
                              Number.parseInt(
                                next.timing.durationMinutes,
                                10
                              ) || 0
                            )
                          : null;
                        setStatusOverride(next.status);
                        updateStatus({
                          value: next.status,
                          date: next.timing.startDate || null,
                          startedAt: next.startedAt,
                          completedAt: next.completedAt,
                          durationMin: durationValue,
                        });
                        utils.tasks.get.setData({ id: task.id }, current =>
                          current
                            ? {
                                ...current,
                                status: next.status,
                                startedAt: next.startedAt,
                                completedAt: next.completedAt,
                              }
                            : current
                        );

                        if (reason === "duration") return;
                        const timingOnlyReason =
                          reason === "start-date" ||
                          reason === "end-date" ||
                          reason === "start-time" ||
                          reason === "end-time";
                        if (timingOnlyReason) {
                          return updateTask.mutate({
                            id: task.id,
                            startedAt: next.startedAt,
                            completedAt: next.completedAt,
                            clientTimestamp: clientTimestamp(),
                          });
                        }
                        const input = {
                          id: task.id,
                          clientTimestamp: clientTimestamp(),
                        };
                        if (reason === "status" && next.status === "pending")
                          return markPending.mutate(input);
                        if (reason === "status" && next.status === "skipped")
                          return markSkipped.mutate(input);
                        if (reason === "status" && next.status === "done") {
                          const timestamp =
                            next.completedAt ?? next.startedAt ?? new Date();
                          return markDone.mutate({
                            id: task.id,
                            clientTimestamp: timestamp.toISOString(),
                          });
                        }
                        updateTask.mutate({
                          id: task.id,
                          status: next.status,
                          startedAt: next.startedAt,
                          completedAt: next.completedAt,
                          clientTimestamp: clientTimestamp(),
                        });
                      }}
                      onCommit={() => saveDuration()}
                    />

                    {visibleFields.repeat && (
                      <RepeatOptions
                        value={repeatRule}
                        onChange={setRepeatRule}
                        showDetails={false}
                        onOverviewClick={() =>
                          openFieldDetails("repeat", "main")
                        }
                      />
                    )}

                    {(visibleFields.notes || visibleFields.log) && (
                      <div className="ui-task-support-buttons">
                        {visibleFields.notes && (
                          <button
                            type="button"
                            onClick={() => openFieldDetails("notes", "main")}
                            className="ui-task-support-button"
                            style={{
                              color: colors.ink,
                              borderColor: colors.bar,
                            }}
                          >
                            <FileText
                              className="h-4 w-4 shrink-0"
                              style={{ color: colors.ink, opacity: 0.6 }}
                            />
                            <span className="min-w-0 flex-1 truncate text-left">
                              {hasNotes ? notes : "Add note"}
                            </span>
                            <ChevronRight
                              className="h-4 w-4 shrink-0"
                              style={{ opacity: 0.5 }}
                            />
                          </button>
                        )}
                        {visibleFields.log && (
                          <button
                            type="button"
                            onClick={() => openFieldDetails("log", "main")}
                            className="ui-task-support-button"
                            style={{
                              color: colors.ink,
                              borderColor: colors.bar,
                            }}
                          >
                            <Logs
                              className="h-4 w-4 shrink-0"
                              style={{ color: colors.ink, opacity: 0.6 }}
                            />
                            <span className="min-w-0 flex-1 truncate text-left">
                              Event log ({logEntryCount})
                            </span>
                            <ChevronRight
                              className="h-4 w-4 shrink-0"
                              style={{ opacity: 0.5 }}
                            />
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* ── SUB VIEWS ── */}
              <div className="ui-task-edit-subviews w-full shrink-0">
                {view === "notes" && (
                  <NoteView
                    colors={colors}
                    hasNotes={hasNotes}
                    savedValue={notes}
                    onSave={(val: string) => {
                      const next = val.trim() === "" ? null : val;
                      const current = notes.trim() === "" ? null : notes;
                      if (next !== current) {
                        const previous = notes;
                        setNotes(next ?? "");
                        utils.tasks.get.setData({ id: task.id }, cached =>
                          cached ? { ...cached, notes: next } : cached
                        );
                        updateTask.mutate(
                          {
                            id: task.id,
                            notes: next,
                            date: task.date ?? null,
                            clientTimestamp: clientTimestamp(),
                          },
                          {
                            onError: () => {
                              setNotes(previous);
                              utils.tasks.get.setData(
                                { id: task.id },
                                cached =>
                                  cached
                                    ? { ...cached, notes: previous || null }
                                    : cached
                              );
                            },
                          }
                        );
                      }
                    }}
                    isSaving={updateTask.isPending}
                    onBack={() => setView(detailReturnView)}
                  />
                )}
                {view === "log" && (
                  <LogView
                    colors={colors}
                    logText={logText}
                    setLogText={setLogText}
                    savedLogRef={savedLogRef}
                    saveLog={saveLog}
                    isSaving={updateTask.isPending}
                    onBack={() => setView(detailReturnView)}
                  />
                )}
                {view === "options" && (
                  <TaskOptionsView
                    task={visibleTask}
                    onEditPattern={
                      task.templateId
                        ? () => {
                            setPatternModalTemplateId(task.templateId ?? null);
                            setPatternModalOpen(true);
                          }
                        : undefined
                    }
                    onConvertToPattern={
                      task.templateId
                        ? undefined
                        : () => {
                            setPatternModalTemplateId(null);
                            setPatternModalOpen(true);
                          }
                    }
                    hasNotes={hasNotes}
                    colors={colors}
                    visibleFields={visibleFields}
                    onToggleVisibility={toggleFieldVisibility}
                    onAddNote={() => openFieldDetails("notes", "options")}
                    onOpenLog={() => openFieldDetails("log", "options")}
                    onOpenRepeat={() => openFieldDetails("repeat", "options")}
                    repeatRule={repeatRule}
                    onOpenDisplay={openDisplayView}
                    onEdit={() => {
                      setView("main");
                      onOpenChange(false);
                      setLocation(`/task/${task.id}`);
                    }}
                    onDuplicate={() => {
                      setView("main");
                      duplicateTask.mutate({
                        id: task.id,
                        date: scheduleDate || null,
                        startTime: startTime || null,
                        durationMin: parseScheduleDuration(durationMin),
                        alertEnabled,
                        title: titleDraft,
                        details: detailsDraft.trim() || null,
                        barColor: colorDraft.inheritView
                          ? null
                          : colorDraft.barColor,
                        bgColor: colorDraft.inheritView
                          ? null
                          : colorDraft.bgColor,
                      });
                    }}
                    onDelete={() => {
                      setView("main");
                      deleteTask.mutate({ ids: [task.id] });
                    }}
                    onBack={() => setView("main")}
                  />
                )}
                {view === "repeat" && (
                  <RepeatView
                    value={repeatRule}
                    onChange={setRepeatRule}
                    savedValue={savedRepeatRule}
                    excludedOccurrenceKeys={excludedRepeatKeys}
                    savedExcludedOccurrenceKeys={savedExcludedRepeatKeys}
                    onExcludedOccurrenceKeysChange={setExcludedRepeatKeys}
                    anchor={{
                      dateTime:
                        repeatQuery.data?.series.sourceTaskId !== undefined &&
                        repeatQuery.data.series.sourceTaskId !== task.id
                          ? repeatQuery.data.series.anchorDateTime
                          : `${scheduleDate || formatDateKey(new Date())}T${startTime || "00:00"}`,
                      alarm:
                        repeatQuery.data?.sourceTask?.alertEnabled ??
                        alertEnabled,
                      duration:
                        repeatQuery.data?.sourceTask?.durationMin ??
                        parseScheduleDuration(durationMin) ??
                        undefined,
                      href: `/task/${repeatQuery.data?.series.sourceTaskId ?? task.id}`,
                    }}
                    colors={colors}
                    isSaving={saveRepeatSeries.isPending}
                    savedAnchorDateTime={
                      repeatQuery.data?.series.anchorDateTime
                    }
                    onSave={(rule, excludedKeys) =>
                      saveRepeatSeries.mutate({
                        id: task.id,
                        rule,
                        excludedOccurrenceKeys: excludedKeys,
                        maxOccurrences: 20,
                      })
                    }
                    onBack={() => setView(detailReturnView)}
                  />
                )}
                {view === "display" && (
                  <DisplayView
                    task={visibleTask}
                    colors={colors}
                    colorDraft={colorDraft}
                    setColorDraft={setColorDraft}
                    onBack={() => {
                      const orig = colorOriginalRef.current;
                      const isDirty =
                        colorDraft.inheritView !== orig.inheritView ||
                        colorDraft.barColor !== orig.barColor ||
                        colorDraft.bgColor !== orig.bgColor;
                      if (isDirty) {
                        updateTask.mutate(
                          {
                            id: task.id,
                            barColor: colorDraft.inheritView
                              ? null
                              : colorDraft.barColor,
                            bgColor: colorDraft.inheritView
                              ? null
                              : colorDraft.bgColor,
                            clientTimestamp: clientTimestamp(),
                          },
                          { onSuccess: () => invalidateTaskLists() }
                        );
                      }
                      setView("main");
                    }}
                  />
                )}
              </div>
            </div>
          </div>
        </SheetContent>
        <PatternEditorModal
          open={patternModalOpen}
          onOpenChange={open => {
            setPatternModalOpen(open);
            if (!open) setPatternModalTemplateId(null);
          }}
          templateId={patternModalTemplateId}
          listId={task.listId ?? null}
          task={task}
          onSaved={() => {
            invalidateTaskLists();
            void utils.tasks.get.invalidate({ id: task.id });
          }}
        />
      </Sheet>

      {/* ── List Picker Modal ── */}
      {areaPickerOpen && (
        <ListSelector
          lists={(listsQuery.data ?? []) as ListOption[]}
          value={visibleTask.listId ?? null}
          onChange={nextListId => {
            updateTask.mutate(
              {
                id: task.id,
                listId: nextListId,
                clientTimestamp: new Date().toISOString(),
              },
              {
                onSuccess: () => {
                  const nextArea = (listsQuery.data ?? []).find(
                    list => list.id === nextListId
                  );
                  if (nextArea) setAreaOverride(nextArea);
                  utils.tasks.get.setData({ id: task.id }, current =>
                    current
                      ? {
                          ...current,
                          listId: nextListId,
                          listName: nextArea?.name ?? current.listName,
                          listColor: nextArea?.color ?? current.listColor,
                          listBgColor: nextArea?.bgColor ?? current.listBgColor,
                        }
                      : current
                  );
                  invalidateTaskLists();
                  onAreaPickerOpenChange?.(false);
                },
              }
            );
          }}
          onClose={() => onAreaPickerOpenChange?.(false)}
          title="Move task to list"
        />
      )}
    </>
  );
}

// ─── Notes View ───────────────────────────────────────────────────────────────
function NoteView({
  colors,
  hasNotes,
  savedValue,
  onSave,
  isSaving,
  onBack,
}: {
  colors: { bar: string; soft: string; ink: string };
  hasNotes: boolean;
  savedValue: string;
  onSave: (val: string) => void;
  isSaving: boolean;
  onBack: () => void;
}) {
  const [draft, setDraft] = useState(savedValue);

  useEffect(() => {
    setDraft(savedValue);
  }, [savedValue]);

  const handleBack = () => {
    if (draft !== savedValue) onSave(draft);
    onBack();
  };

  return (
    <div className="ui-task-note-view mx-auto w-full max-w-2xl pb-6">
      <div className="sticky top-0 z-20 flex items-center gap-3 bg-background px-1 pb-4 pt-4">
        <button
          type="button"
          onClick={handleBack}
          className="inline-flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground hover:bg-accent/40 transition-colors"
          aria-label="Back"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <FileText
          className="h-5 w-5"
          style={{ color: colors.ink, opacity: 0.7 }}
        />
        <span className="font-display text-2xl tracking-tight text-foreground">
          {hasNotes ? "Edit note" : "Add note"}
        </span>
      </div>
      <div className="ui-task-note-editor-wrap w-full px-1">
        <TaskNoteEditor
          savedValue={savedValue}
          onDraftChange={setDraft}
          onSave={value => {
            onSave(value);
            onBack();
          }}
          onCancel={onBack}
          isSaving={isSaving}
          colors={colors}
        />
      </div>
    </div>
  );
}

function LogView({
  colors,
  logText,
  setLogText,
  savedLogRef,
  saveLog,
  isSaving,
  onBack,
}: {
  colors: { bar: string; soft: string; ink: string };
  logText: string;
  setLogText: (v: string | ((prev: string) => string)) => void;
  savedLogRef: React.MutableRefObject<string>;
  saveLog: () => void;
  isSaving: boolean;
  onBack: () => void;
}) {
  return (
    <div className="mx-auto w-full max-w-2xl pb-6">
      <div className="sticky top-0 z-20 flex items-center gap-3 bg-background px-1 pb-4 pt-4">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground hover:bg-accent/40 transition-colors"
          aria-label="Back"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <Logs className="h-5 w-5" style={{ color: colors.ink, opacity: 0.7 }} />
        <span className="font-display text-2xl tracking-tight text-foreground">
          Event log
        </span>
      </div>
      <div className="w-full px-1">
        <TaskLogEditor
          value={logText}
          savedValue={savedLogRef.current}
          onChange={v => setLogText(v)}
          onClear={() => setLogText("")}
          onCancel={() => setLogText(savedLogRef.current)}
          onSave={saveLog}
          isSaving={isSaving}
          accent={colors}
        />
      </div>
    </div>
  );
}

function RepeatView({
  value,
  onChange,
  savedValue,
  excludedOccurrenceKeys,
  savedExcludedOccurrenceKeys,
  onExcludedOccurrenceKeysChange,
  anchor,
  colors,
  savedAnchorDateTime,
  isSaving,
  onSave,
  onBack,
}: {
  value: RepeatRule;
  onChange: (value: RepeatRule) => void;
  savedValue: RepeatRule;
  excludedOccurrenceKeys: string[];
  savedExcludedOccurrenceKeys: string[];
  onExcludedOccurrenceKeysChange: (keys: string[]) => void;
  anchor: { dateTime: string; alarm: boolean; duration?: number; href: string };
  colors: { bar: string; soft: string; ink: string };
  savedAnchorDateTime?: string;
  isSaving: boolean;
  onSave: (value: RepeatRule, excludedOccurrenceKeys: string[]) => void;
  onBack: () => void;
}) {
  return (
    <div className="mx-auto w-full max-w-2xl pb-6">
      <div className="sticky top-0 z-20 flex items-center gap-3 bg-background px-1 pb-4 pt-4">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-accent/40"
          aria-label="Back to options"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <Repeat2
          className="h-5 w-5"
          style={{ color: colors.ink, opacity: 0.7 }}
        />
        <span className="font-display text-2xl tracking-tight text-foreground">
          Repeat
        </span>
      </div>
      <RepeatOptions
        value={value}
        onChange={onChange}
        savedValue={savedValue}
        anchor={anchor}
        savedAnchorDateTime={savedAnchorDateTime}
        excludedOccurrenceKeys={excludedOccurrenceKeys}
        savedExcludedOccurrenceKeys={savedExcludedOccurrenceKeys}
        onExcludedOccurrenceKeysChange={onExcludedOccurrenceKeysChange}
        onSave={onSave}
        isSaving={isSaving}
      />
    </div>
  );
}

// ─── List Picker Modal ────────────────────────────────────────────────────────

function DisplayView({
  task,
  colors,
  colorDraft,
  setColorDraft,
  onBack,
}: {
  task: TaskEditSheetTask;
  colors: { bar: string; soft: string; ink: string };
  colorDraft: TaskColorDraft;
  setColorDraft: (d: TaskColorDraft) => void;
  onBack: () => void;
}) {
  return (
    <div className="mx-auto w-full max-w-2xl pb-6">
      {/* Header */}
      <div className="sticky top-0 z-20 flex items-center gap-3 bg-background px-1 pb-4 pt-4">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground hover:bg-accent/40 transition-colors"
          aria-label="Back"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <Palette
          className="h-5 w-5"
          style={{ color: colors.ink, opacity: 0.7 }}
        />
        <span className="font-display text-2xl tracking-tight text-foreground">
          Display
        </span>
      </div>
      <div className="px-1">
        <TaskColorEditor
          value={colorDraft}
          onChange={setColorDraft}
          onCancel={onBack}
          onSave={onBack}
          previewTitle={task.title}
          previewLabel={task.listName ?? "Inbox"}
          fallbackBar={(task.listColor as string | null) ?? DEFAULT_AREA_COLOR}
          fallbackBg={(task.listBgColor as string | null) ?? null}
          accent={colors}
        />
      </div>
    </div>
  );
}
